import { abbreviate } from '@chainflip/utils/string';
import { Link } from '@/shared/components/atoms/Link';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import ExtrinsicsTable from '@/shared/components/ExtrinsicsTable';
import Button from '@/shared/components/flip-ui-kit/Button';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import Card from '@/shared/components/flip-ui-kit/Card';
import { FundingAndRedemptionsTable } from '@/shared/components/FundingAndRedemptionsTable';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TabPanel } from '@/shared/components/molecules/TabPanel';
import { AuthoritySetHistoryTable } from '@/shared/components/operators/AuthoritySetHistoryTable';
import DelegatorsTable from '@/shared/components/operators/DelegatorsTable';
import ExportHistoryModal from '@/shared/components/operators/ExportHistoryModal';
import { OperatorLogo } from '@/shared/components/operators/Logos';
import { PastValidatorsTable } from '@/shared/components/operators/PastValidatorsTable';
import { PenaltiesTable } from '@/shared/components/operators/PenaltiesTable';
import { ValidatorsTable } from '@/shared/components/operators/ValidatorsTable';
import { WhitelistBlacklistTable } from '@/shared/components/operators/WhitelistBlacklistTable';
import { StarRate } from '@/shared/components/StarRate';
import useBoolean from '@/shared/hooks/useBoolean';
import useOperator from '@/shared/hooks/useOperator';
import useOperatorsAndAuction from '@/shared/hooks/useOperatorsAndAuction';
import { type UseExtrinsicMutationResult } from '@/shared/hooks/useStateChainAccount';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import GiveCoinsIcon from '@/shared/icons/flip-ui-kit/large/GiveCoins';
import KnifeIcon from '@/shared/icons/flip-ui-kit/large/Knife';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/large/MoneyBag';
import PercentageIcon from '@/shared/icons/flip-ui-kit/large/Percentage';
import PulseIcon from '@/shared/icons/flip-ui-kit/large/Pulse';
import PlusIcon from '@/shared/icons/flip-ui-kit/small/Plus';
import { FLIP_SYMBOL, chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import { formatBps, formatPct } from '@/shared/utils/strings';

const OperatorIdPageHeader = ({
  idSs58,
  alias = 'Operator',
}: {
  idSs58: string;
  alias: string | undefined;
}) => (
  <div className="flex items-center gap-x-2">
    <OperatorLogo size="large" idSs58={idSs58} />
    <h1 className="font-aeonikMedium text-24 text-cf-light-3">
      {alias} ({abbreviate(idSs58)})
    </h1>
    <CopyButton textToCopy={idSs58} />
  </div>
);

type Props = {
  idSs58: `cF${string}`;
  actions?: React.ReactNode;
  connectedAccount?: {
    delegationFeePips: number;
    openDelegationEnabled: boolean;
    validators: Map<`cF${string}`, bigint>;
    delegators: Map<`cF${string}`, bigint>;
    exceptionList: `cF${string}`[];
    alias: string | undefined;
  };
  redeemFunds?: (accountId: `cF${string}`) => void;
  fundValidator?: (accountId: `cF${string}`) => void;
  openAddToListModal?: () => void;
  allowDelegator?: UseExtrinsicMutationResult<'operator_allowDelegator'>;
  blockDelegator?: UseExtrinsicMutationResult<'operator_blockDelegator'>;
  operatorType?: 'current' | 'upcoming';
  enableHistoryExport?: boolean;
};

export const OperatorIdPageContent = ({
  idSs58,
  actions,
  connectedAccount,
  redeemFunds,
  fundValidator,
  operatorType,
  openAddToListModal,
  allowDelegator,
  blockDelegator,
  enableHistoryExport = false,
}: Props) => {
  const {
    isLoading,
    validators,
    delegationFeePips,
    delegators,
    openDelegationEnabled,
    exceptionList,
    alias,
    totalSlashed,
    totalRewards,
    reputationScore,
  } = useOperator(idSs58, connectedAccount);
  const { price: flipPrice } = useTokenPrice(chainflipAssetMap.Flip);

  const {
    value: exportModalOpen,
    setTrue: openExportModal,
    setFalse: closeExportModal,
  } = useBoolean();

  const { operators = [] } = useOperatorsAndAuction();

  const summary = operators.find((op) => op.idSs58 === idSs58);

  const accountIds = validators.map((v) => v.idSs58);

  return (
    <div className="flex flex-col gap-6 font-aeonikMedium">
      <div className="flex flex-row flex-wrap justify-between gap-x-2 gap-y-6">
        <div className="flex flex-col gap-2">
          <OperatorIdPageHeader idSs58={idSs58} alias={alias} />
          <div>
            <span className="text-16 text-cf-light-2">Delegation Commission:</span>{' '}
            <span className="text-16 text-cf-white">
              {delegationFeePips !== undefined ? formatBps(delegationFeePips) : <SkeletonLine />}
            </span>
          </div>
        </div>
        <div className="flex gap-x-4">{actions}</div>
      </div>
      {operatorType && (
        <Callout type="info">
          You {operatorType === 'current' ? 'are currently' : 'will be'} delegating to this Operator
          {operatorType === 'upcoming' && ' in the next epoch'}. Go to{' '}
          <Link color="blue" href="/delegate" underline>
            My Delegation
          </Link>{' '}
          to see your statistics.
        </Callout>
      )}
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<MoneyBagIcon className="text-cf-light-2" />}
          title="Total Staked"
          footer="Current"
        >
          <div className="flex items-center gap-x-1">
            {summary?.nextEpoch.totalStake.toFormatted('numeral')} {FLIP_SYMBOL}
            {flipPrice !== undefined && (
              <span className="text-12 text-cf-light-2">
                {formatUsdValue(summary?.nextEpoch.totalStake.mul(flipPrice ?? 0).toNumber())}
              </span>
            )}
          </div>
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<PercentageIcon className="text-cf-light-2" />}
          tooltip="The projected annual percentage yield based on current network conditions, equivalent to the sum of the projected annual Operator commission and Validator rewards earned divided by the total bonded balances of all validators claimed by this operator. Actual returns may vary."
          title="Estimated APY"
          footer="Current"
        >
          {formatPct(summary?.currentEpoch.operatorApy ?? 0)}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<GiveCoinsIcon className="text-cf-light-2" />}
          title="Delegation"
          footer="Current"
          pillProps={
            openDelegationEnabled
              ? {
                  color: 'green',
                  text: 'Delegation Open',
                  maxWidth: false,
                }
              : {
                  color: 'neutral',
                  text: 'Delegation Closed',
                  maxWidth: false,
                }
          }
        >
          <div className="flex items-center gap-x-1">
            {summary?.nextEpoch.totalDelegation.toFormatted('numeral')} {FLIP_SYMBOL}
            {summary?.overdelegation?.gt(0) && (
              <span className="text-12 text-cf-orange-2">
                {summary.overdelegation.toFormatted('numeral')} {FLIP_SYMBOL} overdelegated
              </span>
            )}
            {summary?.requiredDelegation?.gt(0) && (
              <span className="text-12 text-cf-green-3">
                {summary.requiredDelegation.toFormatted('numeral')} {FLIP_SYMBOL} needed
              </span>
            )}
          </div>
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<KnifeIcon className="text-cf-light-2" />}
          tooltip={`The total amount of ${FLIP_SYMBOL} this Operator has lost due to slashing penalties for misbehavior or downtime.`}
          title="Total Slashed"
          footer="All time"
        >
          {totalSlashed?.toFormatted('numeral')} {FLIP_SYMBOL}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<MoneyBagIcon className="text-cf-light-2" />}
          title="Total Rewards"
          footer="All time"
        >
          <div className="flex items-center gap-x-1">
            {totalRewards.amount?.toFormatted('numeral')} {FLIP_SYMBOL}
            <span className="text-12 text-cf-light-2">{formatUsdValue(totalRewards.valueUsd)}</span>
          </div>
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<PulseIcon className="text-cf-light-2" />}
          tooltip="A performance metric reflecting this Operator's reliability, uptime, and behaviour. Higher score indicates better performance."
          title="Reputation Score"
          footer="Current"
        >
          {reputationScore !== undefined ? (
            <div className="flex items-center gap-x-2">
              {reputationScore === 'N/A' ? (
                reputationScore
              ) : (
                <>
                  {reputationScore}/100
                  <StarRate ratePercent={reputationScore} />
                </>
              )}
            </div>
          ) : (
            <SkeletonLine />
          )}
        </Card>
      </div>
      <TabPanel
        contentRight={
          enableHistoryExport && (
            <div className="flex items-center px-4">
              <ExportCsvButton label="Export history" onExport={openExportModal} canExport />
            </div>
          )
        }
        nodes={[
          {
            name: 'Validators',
            count: validators.length,
            content: (
              <ValidatorsTable
                isLoading={isLoading}
                fundValidator={fundValidator}
                redeemFunds={redeemFunds}
                validators={validators}
              />
            ),
          },
          {
            name: 'Delegators',
            count: delegators.size,
            content: <DelegatorsTable blockDelegator={blockDelegator} operatorId={idSs58} />,
          },
          {
            name: openDelegationEnabled ? 'Blacklist' : 'Whitelist',
            count: exceptionList.length,
            content: (
              <WhitelistBlacklistTable
                operatorId={idSs58}
                remove={openDelegationEnabled ? allowDelegator : blockDelegator}
                openDelegationEnabled={openDelegationEnabled}
                whiteOrBlacklistedDelegators={exceptionList}
                delegators={delegators}
              />
            ),
            contentRight: openAddToListModal && (
              <Button
                onClick={openAddToListModal}
                size="small"
                type="secondary-standard"
                className="mr-2 flex items-center gap-x-1"
              >
                <PlusIcon className="text-cf-green-1" />
                Add address
              </Button>
            ),
          },
          {
            name: 'Funding & Redemptions',
            content: <FundingAndRedemptionsTable accountIds={[...accountIds, idSs58]} />,
          },
          {
            name: 'Validator Activity',
            content: <ExtrinsicsTable accountIds={accountIds} />,
          },
          {
            name: 'Authority Set History',
            content: <AuthoritySetHistoryTable operatorId={idSs58} />,
          },
          {
            name: 'Penalty History',
            content: <PenaltiesTable accountIds={accountIds} showValidator isLoading={isLoading} />,
          },
          {
            name: 'Past Validators',
            content: (
              <PastValidatorsTable
                idSs58={idSs58}
                redeemFunds={redeemFunds}
                currentValidators={validators}
              />
            ),
          },
        ]}
      />
      {enableHistoryExport && (
        <ExportHistoryModal
          operatorIdSs58={idSs58}
          open={exportModalOpen}
          onClose={closeExportModal}
        />
      )}
    </div>
  );
};
